import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquareText, Zap, CalendarCheck2 } from "lucide-react";
import { openCalendly } from "@/integrations/calendly";

const HERO_IMG =
  "https://images.unsplash.com/photo-1554774853-719586f82d77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxyZWFsJTIwZXN0YXRlJTIwYWdlbnQlMjBwaG9uZSUyMGxhcHRvcHxlbnwwfHx8fDE3OTA0NzcyMTN8MA&ixlib=rb-4.1.0&q=85";

const flowSteps = [
  { icon: MessageSquareText, label: "New buyer inquiry", cue: "9:47 PM — portal lead" },
  { icon: Zap, label: "Response sent", cue: "in seconds, not hours" },
  { icon: CalendarCheck2, label: "Showing booked", cue: "Thursday, 2:30 PM" },
];

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute -top-48 left-1/4 h-[480px] w-[840px] rounded-full bg-indigo-600/15 blur-[140px]" />

      <div className="relative max-w-6xl mx-auto px-6 pt-32 pb-20 md:pt-40 md:pb-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-16 items-center">
        <div className="text-center lg:text-left">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-testid="hero-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.12]"
          >
            Turn More Real Estate Leads Into{" "}
            <span className="text-gradient">Appointments, Clients &amp; Closings.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            data-testid="hero-subheadline"
            className="mt-6 text-lg md:text-xl text-slate-200 leading-relaxed"
          >
            iVIBES helps real estate agents, teams, and brokers respond to new buyer and seller
            inquiries quickly, follow up consistently, and turn more of the leads you already
            get into booked appointments, clients, and closings.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="mt-8 flex justify-center lg:justify-start"
          >
            <p
              data-testid="hero-brand-statement"
              className="border-l-2 border-cyan-400 pl-4 text-left text-base md:text-lg text-slate-300 font-medium"
            >
              We don't start with technology. We start with the business outcome.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.32 }}
            className="mt-10 flex flex-col sm:flex-row justify-center lg:justify-start gap-4"
          >
            <Link
              to="/lead-flow-audit"
              data-testid="hero-audit-button"
              data-track="homepage_lead_flow_audit_clicked"
              className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white text-lg transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_0_28px_rgba(99,102,241,0.5)]"
            >
              Get Your Free Real Estate Lead Flow Audit
              <ArrowRight size={19} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <a
              href="#how-it-works"
              data-testid="hero-see-how-button"
              data-track="cta_see_how_it_works"
              className="inline-flex items-center justify-center rounded-full px-8 py-4 border-2 border-white/25 hover:border-cyan-400/60 hover:text-cyan-300 font-semibold text-lg transition-[border-color,color,transform] duration-200 hover:-translate-y-1"
            >
              See How It Works
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-6 text-sm text-slate-400"
          >
            Prefer to talk first?{" "}
            <button
              type="button"
              onClick={() => openCalendly()}
              data-testid="hero-review-link"
              data-track="homepage_lead_flow_review_clicked"
              className="text-cyan-300 font-semibold hover:text-cyan-200 underline underline-offset-4 transition-colors duration-200"
            >
              Book Your Lead Flow Review
            </button>
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          data-testid="hero-visual"
          className="relative max-w-md mx-auto w-full lg:max-w-none"
        >
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_32px_80px_rgba(0,0,0,0.5)]">
            <img
              src={HERO_IMG}
              alt="Real estate agent reviewing new inquiries and upcoming appointments"
              className="h-[380px] sm:h-[440px] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/35 to-[#0B0F19]/10" />
            <div
              data-testid="hero-flow-card"
              className="absolute bottom-4 left-4 right-4 rounded-2xl border border-white/15 bg-[#0D1220]/85 backdrop-blur-md p-4 space-y-2.5"
            >
              {flowSteps.map((s, i) => (
                <div key={s.label} className="flex items-center gap-3" data-testid={`hero-flow-step-${i}`}>
                  <div className="h-8 w-8 shrink-0 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center">
                    <s.icon size={15} strokeWidth={2} />
                  </div>
                  <p className="text-sm font-semibold text-white leading-tight">{s.label}</p>
                  <p className="ml-auto text-xs text-slate-400 text-right">{s.cue}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
